import { parseISO } from 'date-fns';

import URLS from 'constants/urls';
import { transformKeysToCamelCase } from 'helpers/core';
import { httpClient } from 'helpers/http';
import { adjustDateForTimezone } from 'helpers/intl-fns';
import { formatOrderId } from 'helpers/sku';

import { PAGE_SIZE } from './SkuPayoutReports.constants';
import { isOrderLineItem, isReservationLineItem } from './SkuPayoutReports.types';

export async function fetchPayouts({ venueId, page = 0, perPage = PAGE_SIZE }) {
  const { data } = await httpClient.get(URLS.reports.payouts({ venue_id: venueId, page, per_page: perPage }));
  return transformKeysToCamelCase(data);
}

// Per the TDD, the list hides incomplete itemization rows client-side so a
// partner never sees a partially-tallied payout. Source of truth is
// `isDetailAvailable` from partners-service, which the BFF derives from
// `itemizationStatus === COMPLETED`. Filtering after pagination can leave a
// page short; product has accepted that tradeoff for milestone 1.
export const filterCompletedItemization = payouts => payouts.filter(p => p.isDetailAvailable);

const joinName = (first, last) => [first, last].filter(Boolean).join(' ');

const parseDateTime = value => {
  if (!value) return null;
  const parsed = parseISO(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Normalize a heterogeneous `PayoutLineItem` into the row model rendered by
 * `SkuPayoutOrdersTable`. Returns `null` for adjustments so the ORDER payout
 * branch stays stable if one slips through.
 *
 * `orderDateTime` arrives from the backend as a UTC ISO-8601 instant and must
 * be displayed in the venue-local timezone (same rule Andrea confirmed for the
 * classes CSV export in PR #3427). `startDate`/`startTime` on reservation
 * line items are already venue wall-clock values, so no conversion is applied.
 *
 * @param {import('./SkuPayoutReports.types').PayoutLineItem} lineItem
 * @param {{ venueTimezone?: string|null }} [options]
 */
export const mapLineItemToOrderRow = (lineItem, { venueTimezone } = {}) => {
  if (isOrderLineItem(lineItem)) {
    const parsed = parseDateTime(lineItem.orderDateTime);
    const dateTime = parsed && venueTimezone ? adjustDateForTimezone(parsed, venueTimezone) : parsed;
    return {
      key: `order-${lineItem.orderId}`,
      id: formatOrderId(lineItem.orderId),
      itemsLabel: (lineItem.items || [])
        .map(item => item.name)
        .filter(Boolean)
        .join(', '),
      dateTime,
      effectiveDate: lineItem.effectiveDate,
      customer: joinName(lineItem.customerFirstName, lineItem.customerLastName),
      amount: lineItem.amountConverted,
      currencyCode: lineItem.currencyCode,
      raw: lineItem,
    };
  }

  if (isReservationLineItem(lineItem)) {
    const { startDate, startTime } = lineItem;
    const combined = startDate && startTime ? `${startDate}T${startTime}` : startDate;
    return {
      key: `res-${lineItem.reservationId}`,
      id: String(lineItem.reservationId),
      itemsLabel: lineItem.className || '',
      dateTime: parseDateTime(combined),
      effectiveDate: lineItem.effectiveDate,
      customer: joinName(lineItem.firstName, lineItem.lastName),
      amount: lineItem.amountConverted,
      currencyCode: lineItem.currencyCode,
      raw: lineItem,
    };
  }

  return null;
};
